import type { DescService } from "@bufbuild/protobuf";
import { createConnectTransport } from "@connectrpc/connect-web";
import { createClient, ConnectError, type Client, type CallOptions, Code } from "@connectrpc/connect";
import { Auth, Chat, AssetService, Export } from "./gen";
import { type Result, ok, err } from "neverthrow";

declare const RUNNING_ENV: "local" | "preview" | "development" | "staging" | "production" | "test";

const getBaseURL = (): Result<string, Error> => {
  if (RUNNING_ENV === "local") {
    // return ok("https://localhost:4000/___controlplane___");
    const tailscaleUrl =
      process.env.TAILSCALE_CP_URL ||
      (typeof window !== "undefined" ? (window as any).__TAILSCALE_CP_URL__ : undefined);
    return ok(tailscaleUrl || "http://localhost:3443");
  }
  if (RUNNING_ENV === "development") {
    return ok("https://api.storytell-development.ai");
  }
  if (RUNNING_ENV === "preview") {
    return err(new Error("Preview environments are not yet implemented"));
  }
  if (RUNNING_ENV === "staging") {
    return ok("https://api.storytell-staging.ai");
  }
  return ok("https://api.storytell.ai");
};

const createTransport = (): Result<ReturnType<typeof createConnectTransport>, Error> => {
  return getBaseURL().map((baseUrl) =>
    createConnectTransport({
      baseUrl,
    }),
  );
};

export class PlatformError {
  private error: ConnectError;

  constructor(error: unknown) {
    this.error = ConnectError.from(error);
  }

  get message(): string {
    return this.error.message;
  }

  get code(): Code {
    return this.error.code;
  }

  get details(): unknown {
    return this.error.details;
  }

  isCanceled(): boolean {
    return this.error.code === Code.Canceled;
  }

  isUnknown(): boolean {
    return this.error.code === Code.Unknown;
  }

  isInvalidArgument(): boolean {
    return this.error.code === Code.InvalidArgument;
  }

  isDeadlineExceeded(): boolean {
    return this.error.code === Code.DeadlineExceeded;
  }

  isNotFound(): boolean {
    return this.error.code === Code.NotFound;
  }

  isAlreadyExists(): boolean {
    return this.error.code === Code.AlreadyExists;
  }

  isPermissionDenied(): boolean {
    return this.error.code === Code.PermissionDenied;
  }

  isResourceExhausted(): boolean {
    return this.error.code === Code.ResourceExhausted;
  }

  isFailedPrecondition(): boolean {
    return this.error.code === Code.FailedPrecondition;
  }

  isAborted(): boolean {
    return this.error.code === Code.Aborted;
  }

  isOutOfRange(): boolean {
    return this.error.code === Code.OutOfRange;
  }

  isUnimplemented(): boolean {
    return this.error.code === Code.Unimplemented;
  }

  isInternal(): boolean {
    return this.error.code === Code.Internal;
  }

  isUnavailable(): boolean {
    return this.error.code === Code.Unavailable;
  }

  isDataLoss(): boolean {
    return this.error.code === Code.DataLoss;
  }

  isUnauthenticated(): boolean {
    return this.error.code === Code.Unauthenticated;
  }
}

export const parseError = (err: unknown): string => {
  const e = ConnectError.from(err);
  return e.message;
};

interface Authorization {
  bearer?: string;
  apiKey?: string;
}

interface Headers {
  authorization: string;
}

export const useHeaders = (auth: Authorization): Result<() => Headers, PlatformError> => {
  if (!auth.apiKey && !auth.bearer) {
    return err(new PlatformError(new Error("either a bearer token or api key must be set to generate headers")));
  }

  try {
    const headers = new Headers();
    if (auth.apiKey) {
      headers.set("x-api-key", auth.apiKey);
    } else {
      headers.set("Authorization", `Bearer ${auth.bearer}`);
    }

    return ok(() => {
      return {
        authorization: headers.get("Authorization") ?? "",
      };
    });
  } catch (error) {
    return err(new PlatformError(error));
  }
};

// Returns the call options based on the modifiers
export const useCallOptions = (...modifiers: Array<(options: CallOptions) => CallOptions>): CallOptions => {
  let options: CallOptions = {};

  for (const modifier of modifiers) {
    options = modifier(options);
  }

  return options;
};

// Create modifier functions
export const withTimeout =
  (timeoutMs: number) =>
  (options: CallOptions): CallOptions => ({
    ...options,
    timeoutMs,
  });

// withHeaders applies options to add headers to the request.
export const withHeaders =
  (headers: HeadersInit) =>
  (options: CallOptions): CallOptions => ({
    ...options,
    headers: { ...options.headers, ...headers },
  });

export const withAbortSignal =
  (signal: AbortSignal) =>
  (options: CallOptions): CallOptions => ({
    ...options,
    signal,
  });

export const withBearerToken =
  (token?: string) =>
  (options: CallOptions): CallOptions => {
    if (!token) {
      return options;
    }
    return {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    };
  };

// Create a type that wraps the methods of a service client to return Result types
export type ResultMethod<T extends (...args: any[]) => Promise<any>> = (
  ...args: Parameters<T>
) => Promise<Result<Awaited<ReturnType<T>>, PlatformError>>;

// Type that represents a client with Result-returning methods. Will never
// throw an error, but will return a Result object that contains either
// the successful result or an error.
export type ResultClient<S extends DescService> = {
  [K in keyof Client<S>]: Client<S>[K] extends (...args: any[]) => Promise<any>
    ? ResultMethod<Client<S>[K]>
    : Client<S>[K];
};

// Initialize the transport once
const transportResult = createTransport();

// Create a wrapper function that takes a method and wraps it to return a Result
function wrapMethod<T extends (...args: any[]) => Promise<any>>(method: T): ResultMethod<T> {
  return async (...args: Parameters<T>) => {
    try {
      const result = await method(...args);
      return ok(result);
    } catch (error) {
      return err(new PlatformError(error));
    }
  };
}

/**
 * Creates a client for the given service with methods that return Result objects
 */
function createResultClient<S extends DescService>(client: Client<S>): ResultClient<S> {
  const resultClient = {} as ResultClient<S>;

  // Get all methods from the client
  for (const key of Object.keys(client)) {
    const method = client[key as keyof Client<S>];

    if (typeof method === "function") {
      resultClient[key as keyof ResultClient<S>] = wrapMethod(method as any) as any;
    } else {
      (resultClient as any)[key] = method;
    }
  }

  return resultClient;
}

// Cache to store created clients
const clientCache = new Map<DescService, ResultClient<any>>();

/**
 * Get a client for the given service that returns Result objects from all methods
 */
export function useClient<S extends DescService>(service: S): ResultClient<S> {
  if (clientCache.has(service)) {
    return clientCache.get(service) as ResultClient<S>;
  }

  if (transportResult.isErr()) {
    console.error("transportResult", transportResult.error);
    throw new PlatformError(transportResult.error);
  }

  const client = createClient(service, transportResult.value);
  const resultClient = createResultClient(client);

  clientCache.set(service, resultClient);
  return resultClient;
}

/**
 * Get a client for the AuthService.
 */
export function useAuthClient(): ResultClient<typeof Auth.AuthService> {
  return useClient(Auth.AuthService);
}

/**
 * Get a client for the AssetService.
 * This service is used for managing assets like files, images, etc.
 */
export function useAssetClient(): ResultClient<typeof AssetService.AssetService> {
  return useClient(AssetService.AssetService);
}

/**
 * Get a client for the ChatService.
 */
export function useChatClient(): ResultClient<typeof Chat.ChatService> {
  return useClient(Chat.ChatService);
}

/**
 * Get a client for the ExportService.
 */
export function useExportClient({
  authToken,
}: { authToken: () => string | undefined }): ResultClient<typeof Export.ExportService> {
  const client = useClient(Export.ExportService);
  return {
    exportRichText: (request, options) => {
      const headers: HeadersInit = options?.headers ?? {};
      if (authToken()) {
        if (Array.isArray(headers)) {
          headers.push(["Authorization", `Bearer ${authToken()}`]);
        } else if (headers instanceof Headers) {
          headers.set("Authorization", `Bearer ${authToken()}`);
        } else {
          headers.Authorization = `Bearer ${authToken()}`;
        }
      }

      return client.exportRichText(request, {
        ...options,
        headers,
      });
    },
  };
}

/**
 * Get a raw client for the ChatService for streaming. Results are
 * not wrapped in a Result Object which simplifies processing streaming
 * updates but requires the caller to handle errors.
 *
 * @internal Use useChatStreamingClient() for type-safe client with auth support
 */
export function useChatStreamingClientRaw() {
  if (transportResult.isErr()) {
    throw new PlatformError(transportResult.error);
  }
  return createClient(Chat.ChatService, transportResult.value);
}

/**
 * Options for streaming chat client method calls, including authentication
 */
export interface ChatClientOptions {
  /** Bearer token for authentication */
  bearerToken?: string;
  /** Request timeout in milliseconds */
  timeoutMs?: number;
  /** Abort signal for request cancellation */
  signal?: AbortSignal;
  /** Additional headers to include */
  headers?: Record<string, string>;
}

/**
 * Type-safe interface for chat client streaming methods
 */
export interface StreamingChatClient {
  /**
   * Start a streaming chat with typed parameters
   * @param request - The stream request with prompt and configuration
   * @param options - Optional client configuration including auth
   * @returns Async iterable of streaming responses
   */
  stream(request: Chat.StreamRequest, options?: ChatClientOptions): AsyncIterable<Chat.StreamResponse>;

  /**
   * Start playback of a previously recorded chat stream
   * @param request - The playback request with message ID and speed
   * @param options - Optional client configuration including auth
   * @returns Async iterable of streaming playback responses
   */
  streamPlayback(
    request: Chat.StreamPlaybackRequest,
    options?: ChatClientOptions,
  ): AsyncIterable<Chat.StreamPlaybackResponse>;
}

/**
 * Get a client for the ChatService for streaming with type safety and auth support.
 * Results are not wrapped in a Result Object which simplifies processing streaming
 * updates but requires the caller to handle errors.
 *
 * @example
 * ```typescript
 * const client = useChatStreamingClient();
 *
 * // Start chat with authentication
 * const stream = client.stream(
 *   newStreamRequest("", "Hello", languageRegion, false),
 *   { bearerToken: "your-auth-token", timeoutMs: 30000 }
 * );
 *
 * // Start playback with authentication
 * const playbackStream = client.streamPlayback(
 *   newStreamPlaybackRequest("message_123", 2.0),
 *   { bearerToken: "your-auth-token" }
 * );
 * ```
 */
export function useChatStreamingClient(): StreamingChatClient {
  const client = useChatStreamingClientRaw();

  return {
    stream: (request, options) => {
      const callOptions = useCallOptions(
        ...(options?.bearerToken ? [withBearerToken(options.bearerToken)] : []),
        ...(options?.timeoutMs ? [withTimeout(options.timeoutMs)] : []),
        ...(options?.signal ? [withAbortSignal(options.signal)] : []),
        ...(options?.headers ? [withHeaders(options.headers)] : []),
      );

      return client.stream(request, callOptions);
    },

    streamPlayback: (request, options) => {
      const callOptions = useCallOptions(
        ...(options?.bearerToken ? [withBearerToken(options.bearerToken)] : []),
        ...(options?.timeoutMs ? [withTimeout(options.timeoutMs)] : []),
        ...(options?.signal ? [withAbortSignal(options.signal)] : []),
        ...(options?.headers ? [withHeaders(options.headers)] : []),
      );

      return client.streamPlayback(request, callOptions);
    },
  };
}
